<script setup lang="ts">
import { ChevronDown } from 'lucide-vue-next'
import { ref } from 'vue'

import type { ProductResponse } from "../../../responses/ProductResponse";
import type { ProductVariantResponse } from "../../../responses/ProductVariantResponse";
import { Money } from "../../../utils/money";
import { label } from "../../mixins/translations";

interface Props {
  course: ProductVariantResponse
  product: ProductResponse
}

const props = defineProps<Props>();

const isOpen = ref(false)
</script>

<template>
  <article class="rounded-md border border-gray-200 bg-white p-2">

    <!-- Mobiel -->
    <div class="lg:hidden">
      <div class="flex items-center gap-4">

        <!-- Datum -->
        <div class="w-16 shrink-0 border-r border-gray-200 pr-2 text-center">
          <div class="text-xl font-extrabold leading-none">
            {{ course.start_date_day }}
          </div>
          <div class="text-xs font-bold text-gray-600">
            {{ course.start_date_month }}
          </div>
        </div>

        <!-- Plaats + prijs -->
        <div class="min-w-0 flex-1">
          <div class="mb-1 flex items-center gap-2">
            <span class="text-base font-extrabold">
              {{ course.location.city }}
            </span>

            <span
                class="shrink-0 text-xs px-2 py-1 text-sm font-bold text-secondary-800"
                v-if="course.product_id !== product.id"
            >
                {{ course.product?.label_linked }}
            </span>
          </div>

          <p class="text-sm leading-normal text-black">
            {{ Money.format(course.price.price_excl, course.price.currency) }}
            <span class="text-gray-500">
                {{ label('site.location.ex_vat') }}
            </span>
          </p>
        </div>

        <!-- Acties -->
        <div class="flex shrink-0 items-center gap-2">
          <a
              :href="course.enrollment_link"
              class="inline-flex justify-center rounded-md bg-primary px-2 py-2 text-xs font-normal text-white hover:bg-primary-400"
          >
            {{ label('site.product.enroll') }}
          </a>

          <button
              type="button"
              class="inline-flex h-8 w-8 items-center justify-center rounded-md bg-gray-100 text-gray-700 transition hover:bg-gray-200"
              :aria-expanded="isOpen"
              @click="isOpen = !isOpen"
          >
            <ChevronDown
                class="h-6 w-6 transition-transform duration-200"
                :class="{ 'rotate-180': isOpen }"
            />
          </button>
        </div>
      </div>

      <!-- Uitklapgedeelte mobiel -->
      <div
          v-show="isOpen"
          class="mt-3 grid gap-5 border-t border-gray-100 pt-3 sm:grid-cols-2"
      >
        <div>
          <p class="text-sm leading-normal text-gray-500">
                        <span class="font-bold text-black">
                            {{ course.location.name }}
                        </span><br />
            {{ course.location.address }}<br />
            {{ course.location.postal }} {{ course.location.city }}

            <span
                class="ml-2 rounded bg-gray-50 px-2 py-1 text-xs font-semibold text-gray-800"
                v-show="course.location_distance > 0"
            >
                            {{ course.location_distance }} KM
                        </span>
          </p>
        </div>

        <div class="text-sm">
          <strong>{{ label('site.location.course_days') }}</strong>

          <div
              v-for="day in course.planning_formatted"
              :key="`${day.date}-${day.start}-${day.end}`"
              class="flex gap-4 text-black"
          >
            <span>{{ day.date }}</span>
            <span>{{ day.start }} - {{ day.end }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Desktop layout -->
    <div class="hidden gap-4 lg:flex lg:flex-row lg:items-center p-4">
      <div class="flex justify-center lg:w-1/12">
        <div class="text-center">
          <div class="text-4xl font-extrabold">
            {{ course.start_date_day }}
          </div>
          <div class="text-sm text-gray-600">
            {{ course.start_date_month }}
          </div>
        </div>
      </div>

      <div class="lg:w-4/12">
        <div class="mb-1 flex items-center gap-2">
          <h3 class="text-lg font-extrabold">
            {{ course.location.city }}
          </h3>

          <span
              class="px-2 py-1 text-sm font-bold text-secondary-800"
              v-if="course.product_id !== product.id"
          >
                {{  course.product?.label_linked }}
            </span>
        </div>

        <p class="text-sm leading-normal text-gray-500">
          <span class="font-bold">{{ course.location.name }}</span><br />
          {{ course.location.address }}<br />
          {{ course.location.postal }} {{ course.location.city }}

          <span
              class="ml-2 rounded bg-gray-50 px-2 py-1 text-xs font-semibold text-gray-800"
              v-show="course.location_distance > 0"
          >
                        {{ course.location_distance }} KM
                    </span>
        </p>
      </div>

      <div class="lg:w-3/12">
        <p class="text-sm">
          {{ Money.format(course.price.price_excl, course.price.currency) }}
          <span class="text-gray-500">
                        {{ label('site.location.ex_vat') }}
                    </span>
        </p>

        <div class="mt-2 text-md">
          <strong>{{ label('site.location.course_days') }}</strong>

          <div
              v-for="day in course.planning_formatted"
              :key="`${day.date}-${day.start}-${day.end}`"
              class="flex gap-4 text-black"
          >
            <span>{{ day.date }}</span>
            <span>{{ day.start }} - {{ day.end }}</span>
          </div>
        </div>
      </div>

      <div class="lg:ml-auto">
        <a
            :href="course.enrollment_link"
            class="inline-flex w-full justify-center rounded-md bg-primary px-8 py-2 font-normal text-white hover:bg-primary-400 lg:w-auto"
        >
          {{ label('site.product.enroll') }}
        </a>
      </div>
    </div>

  </article>
</template>
